import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  useTheme,
  alpha
} from '@mui/material';
import {
  Lightbulb as VisionIcon,
  Psychology as MissionIcon,
  Groups as TeamIcon
} from '@mui/icons-material';

const AboutPage = () => {
  const theme = useTheme();

  const sections = [
    {
      title: 'Our Vision',
      description: 'To make IT skills genuinely accessible — not just to people who already love tech, but to anyone whose job, career, or life would be better with them.',
      icon: <VisionIcon />,
      color: theme.palette.primary.main,
      points: [
        'Turn abstract technical concepts into plain, practical knowledge',
        'Build confidence in beginners who were told "IT is not for me"',
        'Create a learning experience that feels human, not clinical'
      ]
    },
    {
      title: 'Our Mission',
      description: 'To guide learners from zero to capable across the IT skills that matter most — Python, SQL, Data Analysis, Azure, and more — one clear step at a time.',
      icon: <MissionIcon />,
      color: theme.palette.secondary.main,
      points: [
        'Structured learning paths that build on each other logically',
        'Real-world tasks and worked examples you can use immediately',
        'Multiple ways to understand each idea — because one explanation is never enough'
      ]
    },
    {
      title: 'Our Team',
      description: 'A small team of practitioners who have spent years helping non-technical colleagues understand data, automation, and cloud tools from scratch.',
      icon: <TeamIcon />,
      color: theme.palette.success.main,
      points: [
        'Industry professionals with real-world experience to share',
        'Educators who explain things clearly without dumbing them down',
        'Builders who keep improving the platform based on what learners actually need'
      ]
    }
  ];

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        py: { xs: 6, sm: 8, md: 12 }
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: { xs: 6, sm: 8 } }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 800,
              mb: 2,
              fontSize: { xs: '2rem', sm: '2.75rem', md: '3rem' },
              background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            About Qode Clarity
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 640, mx: 'auto' }}
          >
            We help people who do not think of themselves as "IT people" become genuinely capable with
            the tools that matter — Python, SQL, data analysis, cloud, and more.
            Step by step, in plain language, with real tasks you can practise right away.
          </Typography>
        </Box>

        <Grid container spacing={4}>
          {sections.map((section) => (
            <Grid item xs={12} md={4} key={section.title}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, md: 4 },
                  height: '100%',
                  borderRadius: 4,
                  border: 1,
                  borderColor: alpha(section.color, 0.2),
                  bgcolor: alpha(section.color, 0.03),
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[4],
                    borderColor: alpha(section.color, 0.3),
                    bgcolor: alpha(section.color, 0.05)
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: alpha(section.color, 0.1),
                      color: section.color,
                      display: 'flex'
                    }}
                  >
                    {section.icon}
                  </Box>
                  <Typography variant="h5" fontWeight={700} color={section.color}>
                    {section.title}
                  </Typography>
                </Box>

                <Typography variant="body1" paragraph color="text.secondary">
                  {section.description}
                </Typography>

                <Box component="ul" sx={{ pl: 2, mb: 0 }}>
                  {section.points.map((point) => (
                    <Typography
                      key={point}
                      component="li"
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {point}
                    </Typography>
                  ))}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default AboutPage;
